import { useCallback, useEffect, useRef, useState } from 'react';
import { apiBaseURL, getQualityAudioUrl } from '../constants/routes';

// Streaming quality for the player: the user's saved preference, switching
// it mid-track, buffering state, and a "step down?" suggestion when the
// connection can't keep up. Users always make the final call - this only
// ever suggests, it never changes quality on its own.

export const QUALITY_LABELS = {
  high: 'Lossless',
  medium: '320k',
  low: '128k',
};

export const QUALITY_DESCRIPTIONS = {
  high: 'Original upload. Uses the most data.',
  medium: 'Near-transparent MP3. The default.',
  low: 'Smallest. Best on slow or metered connections.',
};

const STEP_DOWN = { high: 'medium', medium: 'low' };

// Mid-playback stalls (not the initial load) within this window before
// suggesting a lower quality
const STALLS_BEFORE_SUGGESTING = 3;
const STALL_WINDOW_MS = 60 * 1000;

const DISMISSED_KEY = 'tuneswell:quality-suggestion-dismissed';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}`, 'Content-Type': 'application/json' } : null;
};

const isSlowConnection = () => {
  const connection = navigator.connection;
  if (!connection) return false;
  return connection.saveData || ['slow-2g', '2g', '3g'].includes(connection.effectiveType);
};

const wasDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const useStreamingQuality = ({ audioRef, currentTrack, isAuthed }) => {
  const [streamingQuality, setStreamingQuality] = useState('medium');
  const [isBuffering, setIsBuffering] = useState(false);
  // { quality, reason: 'buffering' | 'connection' } or null
  const [qualitySuggestion, setQualitySuggestion] = useState(null);
  const stallTimesRef = useRef([]);
  const qualityRef = useRef(streamingQuality);
  qualityRef.current = streamingQuality;

  // Load the saved preference once signed in
  useEffect(() => {
    const headers = authHeaders();
    if (!isAuthed || !headers) return;
    let cancelled = false;
    fetch(`${apiBaseURL}/auth/streaming-quality/`, { headers })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled && data?.streaming_quality) setStreamingQuality(data.streaming_quality);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthed]);

  const suggest = useCallback((reason) => {
    const lower = STEP_DOWN[qualityRef.current];
    if (!lower || wasDismissed()) return;
    setQualitySuggestion((current) => current || { quality: lower, reason });
  }, []);

  // Track buffering, and suggest stepping down after repeated stalls
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleWaiting = () => {
      setIsBuffering(true);
      // Stalls during the initial load are expected; only count ones that
      // interrupt playback
      if (audio.currentTime < 1) return;
      const now = Date.now();
      stallTimesRef.current = [...stallTimesRef.current, now].filter((t) => now - t < STALL_WINDOW_MS);
      if (stallTimesRef.current.length >= STALLS_BEFORE_SUGGESTING) suggest('buffering');
    };
    const handleReady = () => setIsBuffering(false);

    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('playing', handleReady);
    audio.addEventListener('canplay', handleReady);
    audio.addEventListener('pause', handleReady);
    return () => {
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('playing', handleReady);
      audio.removeEventListener('canplay', handleReady);
      audio.removeEventListener('pause', handleReady);
    };
  }, [audioRef, suggest]);

  // A browser that reports data saver / a slow network gets the suggestion
  // as soon as music starts, rather than after it has already stalled
  useEffect(() => {
    if (currentTrack && isAuthed && isSlowConnection()) suggest('connection');
  }, [currentTrack, isAuthed, suggest]);

  // New track, fresh stall count
  useEffect(() => {
    stallTimesRef.current = [];
  }, [currentTrack?.id]);

  const dismissQualitySuggestion = useCallback(() => {
    setQualitySuggestion(null);
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch (e) {
      // Storage unavailable - the suggestion may come back, which is fine
    }
  }, []);

  // Save the preference and, if something is loaded, swap the current
  // track's source in place, keeping its position and play state
  const changeStreamingQuality = useCallback(async (quality) => {
    const headers = authHeaders();
    if (!headers || quality === qualityRef.current) return false;

    const response = await fetch(`${apiBaseURL}/auth/streaming-quality/`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ streaming_quality: quality }),
    }).catch(() => null);
    if (!response?.ok) return false;

    setStreamingQuality(quality);
    setQualitySuggestion(null);
    stallTimesRef.current = [];

    const audio = audioRef.current;
    if (currentTrack?.id && audio?.src) {
      const url = await getQualityAudioUrl(currentTrack.id);
      if (url && url !== audio.src) {
        const position = audio.currentTime;
        const wasPlaying = !audio.paused;
        audio.src = url;
        audio.addEventListener('loadedmetadata', () => {
          audio.currentTime = position;
          if (wasPlaying) audio.play().catch(() => {});
        }, { once: true });
        audio.load();
      }
    }
    return true;
  }, [audioRef, currentTrack]);

  return {
    streamingQuality,
    changeStreamingQuality,
    isBuffering,
    qualitySuggestion,
    dismissQualitySuggestion,
  };
};
